import { BaseComponent, ComponentConfig } from './base-component';
import { triggerEvent } from './util/event-handler';
import { DatePickerAnchor } from './datepicker';

/**
 * Date picker for projects that render their own HTML (MVC, Razor Pages, plain
 * pages) rather than Blazor.
 *
 * The design system already describes the calendar in CSS and implements it as
 * a Blazor component, but a server-rendered project has no way to reach that:
 * it ends up either shipping a third-party widget whose markup is unrelated to
 * ours, or hand-writing the calendar per project. Both were happening. This
 * builds the same markup the Blazor component does, so the appearance comes
 * from the stylesheet in both cases and there is one calendar to maintain.
 *
 * Positioning and dismissal are delegated to {@link DatePickerAnchor}, which
 * puts the panel in the top layer so a clipping ancestor such as a `.card`
 * cannot cut it off.
 *
 * Usage:
 * ```html
 * <input type="date" data-antumo-toggle="datepicker" name="saleDate">
 * ```
 * The input is switched to `type="text"` so the browser does not also render
 * its own picker; the value stays ISO (`yyyy-MM-dd`) either way.
 */
export class DatePicker extends BaseComponent {
  static get NAME() { return 'datepicker'; }

  static DefaultConfig: ComponentConfig = {
    /** BCP-47 tag for month and weekday names. Defaults to the document language. */
    locale: null,
    /** Earliest selectable date, ISO. */
    min: null,
    /** Latest selectable date, ISO. */
    max: null,
    /** Label for the button that clears the value. */
    clearLabel: 'Clear',
    /** Label for the button that selects today. */
    todayLabel: 'Today',
  };

  private _input!: HTMLInputElement;
  private _root!: HTMLElement;
  private _panel!: HTMLElement;
  private _selected: Date | null = null;
  private _visibleMonth!: Date;
  private _open = false;
  private _anchor: DatePickerAnchor | null = null;
  private _panelClickHandler!: (event: Event) => void;
  private _dismissHandler!: (event: Event) => void;

  constructor(element: Element, config: ComponentConfig = {}) {
    super(element, config);
    if ((this as unknown as { _input?: HTMLInputElement })._input) return;

    this._input = element as HTMLInputElement;
    this._selected = parseIso(this._input.value);
    this._visibleMonth = startOfMonth(this._selected ?? new Date());

    this._prepareField();
    this._buildPanel();
    this._bind();
  }

  /**
   * Wraps the field so the panel is a sibling of the trigger, which is the
   * structure DatePickerAnchor expects, and stops the browser rendering its own
   * picker beside ours.
   */
  private _prepareField(): void {
    const input = this._input;
    input.type = 'text';
    input.autocomplete = 'off';
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-haspopup', 'dialog');
    input.setAttribute('aria-expanded', 'false');

    const root = document.createElement('div');
    root.className = 'antumo-datepicker';
    root.id = `antumo-datepicker-root-${uid()}`;
    input.parentNode!.insertBefore(root, input);

    const trigger = document.createElement('div');
    trigger.className = 'antumo-datepicker-trigger';
    trigger.appendChild(input);
    root.appendChild(trigger);

    this._root = root;
  }

  private _buildPanel(): void {
    const panel = document.createElement('div');
    panel.className = 'datepicker datepicker-md antumo-datepicker-panel';
    panel.id = `antumo-datepicker-panel-${uid()}`;
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'false');
    panel.setAttribute('popover', 'manual');
    this._root.appendChild(panel);
    this._panel = panel;
    this._input.setAttribute('aria-controls', panel.id);
  }

  private get _locale(): string {
    return (this._config.locale as string)
      || document.documentElement.getAttribute('lang')
      || 'en';
  }

  /** Weekday order follows the locale, matching the Blazor component. */
  private get _firstDayOfWeek(): number {
    // Intl exposes this only behind a flag in some engines, so fall back to
    // Monday, which is what every locale this system ships for uses.
    const info = (Intl as unknown as {
      Locale?: new (tag: string) => { weekInfo?: { firstDay?: number } };
    }).Locale;
    if (info) {
      try {
        const first = new info(this._locale).weekInfo?.firstDay;
        if (typeof first === 'number') return first % 7; // 7 = Sunday in weekInfo
      } catch { /* locale not understood; fall through */ }
    }
    return 1;
  }

  private _render(): void {
    const view = this._visibleMonth;
    const today = new Date();
    const first = startOfMonth(view);
    const leading = mod(first.getDay() - this._firstDayOfWeek, 7);
    const start = addDays(first, -leading);
    const min = parseIso(this._config.min as string);
    const max = parseIso(this._config.max as string);

    const parts: string[] = [];

    parts.push('<div class="datepicker-nav">');
    parts.push(navButton('previous', -1));
    parts.push(
      `<button type="button" class="datepicker-nav-title">${escapeHtml(
        formatMonth(view, this._locale)
      )}</button>`
    );
    parts.push(navButton('next', 1));
    parts.push('</div>');

    parts.push('<div class="datepicker-weekdays">');
    for (let i = 0; i < 7; i++) {
      const day = addDays(start, i);
      parts.push(
        `<span class="datepicker-weekday" aria-hidden="true">${escapeHtml(
          formatWeekday(day, this._locale)
        )}</span>`
      );
    }
    parts.push('</div>');

    parts.push(`<div class="datepicker-grid" role="grid">`);
    for (let offset = 0; offset < 42; offset++) {
      const day = addDays(start, offset);
      const outOfRange = (min !== null && day < min) || (max !== null && day > max);

      let css = 'datepicker-day';
      if (day.getMonth() !== view.getMonth()) css += ' datepicker-day-outside-month';
      if (sameDay(day, this._selected)) css += ' datepicker-day-selected';
      else if (sameDay(day, today)) css += ' datepicker-day-today';
      if (outOfRange) css += ' datepicker-day-disabled';

      parts.push(
        `<button type="button" class="${css}" role="gridcell" data-date="${toIso(day)}"` +
        `${outOfRange ? ' disabled' : ''}` +
        `${sameDay(day, today) ? ' aria-current="date"' : ''}` +
        ` aria-selected="${sameDay(day, this._selected)}">${day.getDate()}</button>`
      );
    }
    parts.push('</div>');

    parts.push('<div class="datepicker-actions">');
    parts.push(
      `<button type="button" class="datepicker-action-button datepicker-action-button-secondary" ` +
      `data-action="clear">${escapeHtml(this._config.clearLabel as string)}</button>`
    );
    parts.push(
      `<button type="button" class="datepicker-action-button datepicker-action-button-primary" ` +
      `data-action="today">${escapeHtml(this._config.todayLabel as string)}</button>`
    );
    parts.push('</div>');

    this._panel.innerHTML = parts.join('');
  }

  private _bind(): void {
    const open = () => this.show();
    this._input.addEventListener('click', open);
    this._input.addEventListener('focus', open);

    this._input.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape') this.hide();
      if (event.key === 'ArrowDown' && !this._open) {
        event.preventDefault();
        this.show();
      }
    });

    // The panel's contents are replaced on every render, so listen on the panel
    // itself rather than on the buttons, which would be detached by then.
    this._panelClickHandler = (event: Event) => {
      const target = event.target as Element;

      const nav = target.closest('[data-nav]');
      if (nav) {
        const step = Number((nav as HTMLElement).dataset.nav);
        this._visibleMonth = addMonths(this._visibleMonth, step);
        this._render();
        this._anchor?.reposition();
        return;
      }

      const day = target.closest('[data-date]');
      if (day) {
        this.select(parseIso((day as HTMLElement).dataset.date!)!);
        return;
      }

      const action = target.closest('[data-action]');
      if (!action) return;
      if ((action as HTMLElement).dataset.action === 'today') this.select(new Date());
      else this.clear();
    };
    this._panel.addEventListener('click', this._panelClickHandler);

    // DatePickerAnchor reports an outside press through this event when it has
    // no .NET reference to call back into.
    this._dismissHandler = () => this.hide();
    this._root.addEventListener('antumo:datepicker:dismiss', this._dismissHandler);
  }

  show(): void {
    if (this._open) return;
    this._selected = parseIso(this._input.value) ?? this._selected;
    if (this._selected) this._visibleMonth = startOfMonth(this._selected);
    this._render();
    this._root.classList.add('antumo-datepicker-open');
    this._input.setAttribute('aria-expanded', 'true');
    this._open = true;
    this._anchor = new DatePickerAnchor(this._root);
    triggerEvent(this._element!, 'antumo:datepicker:shown', {}, false);
  }

  hide(): void {
    if (!this._open) return;
    this._anchor?.dispose();
    this._anchor = null;
    this._root.classList.remove('antumo-datepicker-open');
    this._input.setAttribute('aria-expanded', 'false');
    this._open = false;
    triggerEvent(this._element!, 'antumo:datepicker:hidden', {}, false);
  }

  isOpen(): boolean { return this._open; }

  /** Selects a date, writes it back to the field and closes the panel. */
  select(date: Date): void {
    this._selected = date;
    this._visibleMonth = startOfMonth(date);
    this._input.value = toIso(date);
    this._input.dispatchEvent(new Event('input', { bubbles: true }));
    this._input.dispatchEvent(new Event('change', { bubbles: true }));
    triggerEvent(this._element!, 'antumo:datepicker:select', { value: toIso(date) }, false);
    this.hide();
  }

  clear(): void {
    this._selected = null;
    this._input.value = '';
    this._input.dispatchEvent(new Event('input', { bubbles: true }));
    this._input.dispatchEvent(new Event('change', { bubbles: true }));
    this.hide();
  }

  dispose(): void {
    this._panel?.removeEventListener('click', this._panelClickHandler);
    this._root?.removeEventListener('antumo:datepicker:dismiss', this._dismissHandler);
    this._anchor?.dispose();
    super.dispose();
  }

  /** Claims every `[data-antumo-toggle="datepicker"]` and every `input[type=date]`. */
  static autoInit(root: ParentNode = document): void {
    root
      .querySelectorAll('[data-antumo-toggle="datepicker"], input[type="date"]')
      .forEach((el) => DatePicker.getOrCreateInstance(el));
  }
}

function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

function mod(value: number, by: number): number {
  return ((value % by) + by) % by;
}

function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

function toIso(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function parseIso(value: string | null | undefined): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) return new Date(+match[1], +match[2] - 1, +match[3]);
  const loose = new Date(value);
  return Number.isNaN(loose.getTime()) ? null : loose;
}

function sameDay(a: Date | null, b: Date | null): boolean {
  return !!a && !!b
    && a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + days);
  return next;
}

function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function formatMonth(date: Date, locale: string): string {
  return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
}

function formatWeekday(date: Date, locale: string): string {
  return date.toLocaleDateString(locale, { weekday: 'short' }).replace(/\.$/, '');
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[char]!));
}

function navButton(direction: 'previous' | 'next', step: number): string {
  const path = step < 0 ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6';
  return `<button type="button" class="datepicker-nav-button" data-nav="${step}" ` +
    `aria-label="${direction === 'previous' ? 'Previous month' : 'Next month'}">` +
    `<svg class="datepicker-nav-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" ` +
    `stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" ` +
    `aria-hidden="true"><path d="${path}"/></svg></button>`;
}
